/**
 * SpeedButtonBar Component
 * Button-based velocity control for Netflix-style player
 *
 * Features:
 * - Preset speed buttons: 50, 24, 12, 6, 1 fps (forward and backward)
 * - Central pause button
 * - Visual feedback for active speed
 * - Aligned with video export standards (24fps cinema)
 */

import { MaterialIcon } from '@/components/MaterialIcon';
import styles from './SpeedButtonBar.module.css';

interface SpeedButtonBarProps {
  onVelocityChange: (photosPerSecond: number) => void;
  onTogglePlay: () => void;
  currentVelocity: number;
  disabled?: boolean;
}

const SPEEDS = [50, 24, 12, 6, 1];

export function SpeedButtonBar({
  onVelocityChange,
  onTogglePlay,
  currentVelocity,
  disabled = false,
}: SpeedButtonBarProps) {
  const handleSpeedClick = (speed: number) => {
    if (disabled) return;
    // If clicking the same speed, toggle to pause
    if (Math.abs(currentVelocity - speed) < 0.1) {
      onVelocityChange(0);
    } else {
      onVelocityChange(speed);
    }
  };

  const isPaused = Math.abs(currentVelocity) < 0.1;

  return (
    <div className={`${styles.speedButtonBar} ${disabled ? styles.disabled : ''}`}>
      {/* Backward speeds (reversed order: -50, -24, -12, -6, -1) */}
      <div className={styles.speedButtonsGroup}>
        {SPEEDS.map((speed) => {
          const isActive = Math.abs(currentVelocity + speed) < 0.1;
          return (
            <button
              key={`back-${speed}`}
              className={`${styles.speedButton} ${isActive ? styles.active : ''}`}
              onClick={() => handleSpeedClick(-speed)}
              disabled={disabled}
              title={`Rewind at ${speed} fps`}
            >
              -{speed}
            </button>
          );
        })}
      </div>

      {/* Play/Pause icon */}
      <button
        className={styles.pauseButton}
        onClick={onTogglePlay}
        disabled={disabled}
        title={isPaused ? 'Paused' : 'Pause'}
      >
        <MaterialIcon
          name={isPaused ? 'play_circle' : 'pause_circle'}
          size={38}
          filled
          style={{ color: '#1890ff' }}
        />
      </button>

      {/* Forward speeds (1, 6, 12, 24, 50) */}
      <div className={styles.speedButtonsGroup}>
        {[...SPEEDS].reverse().map((speed) => {
          const isActive = Math.abs(currentVelocity - speed) < 0.1;
          return (
            <button
              key={`fwd-${speed}`}
              className={`${styles.speedButton} ${isActive ? styles.active : ''}`}
              onClick={() => handleSpeedClick(speed)}
              disabled={disabled}
              title={`Play at ${speed} fps`}
            >
              +{speed}
            </button>
          );
        })}
      </div>
    </div>
  );
}
